import React from 'react';

interface BirthdayCelebrationProps {
  isOpen: boolean;
  onClose: () => void;
  isBadgeEarned?: boolean; // true if they already earned the badge, false if it's an opportunity
}

const BirthdayCelebration: React.FC<BirthdayCelebrationProps> = ({ 
  isOpen, 
  onClose, 
  isBadgeEarned = false 
}) => {
  const playHappyBirthday = () => {
    try {
      // Try to play Happy Birthday audio from public folder
      const audio = new Audio('/happy-birthday.mp3');
      audio.volume = 0.7;
      audio.play().catch(err => {
        console.log('Happy Birthday audio not available, using fallback');
        // Fallback: play a simple beep pattern
        playBirthdayBeep();
      });
    } catch (error) {
      console.log('Audio not supported, using fallback');
      playBirthdayBeep();
    }
  };

  const playBirthdayBeep = () => {
    // Simple beep pattern for Happy Birthday melody
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const playTone = (frequency: number, duration: number, delay: number) => {
      setTimeout(() => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration);
      }, delay);
    };

    // Happy Birthday melody (simplified)
    const melody = [
      { freq: 523, duration: 0.4, delay: 0 },      // C5
      { freq: 523, duration: 0.2, delay: 400 },    // C5
      { freq: 659, duration: 0.8, delay: 600 },    // E5
      { freq: 523, duration: 0.8, delay: 1400 },   // C5
      { freq: 698, duration: 0.8, delay: 2200 },   // F5
      { freq: 659, duration: 1.6, delay: 3000 },   // E5
    ];

    melody.forEach(note => {
      playTone(note.freq, note.duration, note.delay);
    });
  };

  // Play audio when popup opens
  React.useEffect(() => {
    if (isOpen) {
      playHappyBirthday();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-gradient-to-br from-pink-100 via-purple-50 to-pink-100 rounded-3xl p-8 max-w-md mx-4 shadow-2xl border-4 border-pink-200 gentle-entrance">
        <div className="text-center">
          {/* Birthday Cake with Candles */}
          <div className="mb-6 relative">
            <div className="text-6xl mb-4">🎂</div>
            {/* Animated candles */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-2">
              <div className="flex space-x-1">
                <div className="text-lg candle-flicker">🕯️</div>
                <div className="text-lg candle-flicker" style={{animationDelay: '0.2s'}}>🕯️</div>
                <div className="text-lg candle-flicker" style={{animationDelay: '0.4s'}}>🕯️</div>
              </div>
            </div>
          </div>

          {/* Birthday Message */}
          <h2 className="text-3xl font-bold text-pink-600 mb-2">
            🎉 Happy Birthday! 🎉
          </h2>
          
          {isBadgeEarned ? (
            // Already earned badge message
            <>
              <p className="text-lg text-gray-700 mb-4">
                You practised on your birthday!
              </p>
              <p className="text-xl font-semibold text-purple-600 mb-6">
                What amazing dedication! 🌟
              </p>
            </>
          ) : (
            // Opportunity message
            <>
              <p className="text-lg text-gray-700 mb-4">
                It's your birthday! 🎂
              </p>
              <p className="text-xl font-semibold text-purple-600 mb-2">
                Practice today and earn a special badge! 🌟
              </p>
              <p className="text-sm text-gray-600 mb-6">
                Complete any practice session to become a Birthday Practice Star!
              </p>
            </>
          )}

          {/* Floating balloons animation */}
          <div className="absolute -top-4 -left-4 text-2xl animate-pulse" style={{animationDelay: '0.5s'}}>🎈</div>
          <div className="absolute -top-6 -right-4 text-2xl animate-pulse" style={{animationDelay: '0.7s'}}>🎈</div>
          <div className="absolute top-8 -left-6 text-2xl animate-pulse" style={{animationDelay: '0.9s'}}>🎈</div>
          <div className="absolute top-6 -right-6 text-2xl animate-pulse" style={{animationDelay: '1.1s'}}>🎈</div>

          {/* Birthday Badge Display */}
          <div className="bg-gradient-to-r from-pink-400 to-purple-600 text-white px-6 py-3 rounded-xl mb-6 shadow-lg">
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">🎂</span>
              <span className="font-bold text-lg">
                {isBadgeEarned ? 'Birthday Practice Star' : 'Birthday Practice Star (Earnable!)'}
              </span>
              <span className="text-2xl">🎂</span>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            {isBadgeEarned ? '🕯️ Blow out your candles and make a wish! 🕯️' : '🎂 Let\'s celebrate and practice! 🎂'}
          </button>

          {/* Confetti effect */}
          <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
            <div className="confetti-piece" style={{left: '10%', animationDelay: '0s'}}>🎊</div>
            <div className="confetti-piece" style={{left: '20%', animationDelay: '0.2s'}}>🎉</div>
            <div className="confetti-piece" style={{left: '30%', animationDelay: '0.4s'}}>🎊</div>
            <div className="confetti-piece" style={{left: '40%', animationDelay: '0.6s'}}>🎉</div>
            <div className="confetti-piece" style={{left: '50%', animationDelay: '0.8s'}}>🎊</div>
            <div className="confetti-piece" style={{left: '60%', animationDelay: '1s'}}>🎉</div>
            <div className="confetti-piece" style={{left: '70%', animationDelay: '1.2s'}}>🎊</div>
            <div className="confetti-piece" style={{left: '80%', animationDelay: '1.4s'}}>🎉</div>
            <div className="confetti-piece" style={{left: '90%', animationDelay: '1.6s'}}>🎊</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BirthdayCelebration;
